import { useCallback } from "react";
import { type ListRenderItemInfo } from "react-native";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import Animated from "react-native-reanimated";
import { Pagination } from "@visioni/a3s-ui";
import { View, Text } from "@visioni/a3s-ui/components";
import { CompilationRow, CompilationRowSkeleton, type CompilationRowStatus } from "../CompilationRow";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CompilationListItem {
  readonly id: string;
  /**
   * Site and camera the compilation belongs to.
   *
   * Carried on the row so a "watch" link can address the compilation on its
   * own, rather than only while this row happens to be on the page in front
   * of the user — the endpoint that resolves one by id is camera-scoped, so
   * the id alone is not enough to fetch it back.
   */
  readonly siteId: string;
  readonly cameraKey: string;
  readonly status: CompilationRowStatus;
  readonly cameraLabel: string;
  readonly requestedAt: string;
  readonly rangeStart: string;
  readonly rangeEnd: string;
  /**
   * Set when a `queued` job has sat long enough that it probably never
   * reached the edge. Surfaced as a warning on the row rather than as a
   * `failed` status, so the row still matches the server-side status filter
   * that returned it.
   */
  readonly staleQueued?: boolean | undefined;
  /**
   * Why the compilation failed, already turned into prose — only present when
   * `status` is `"failed"` and the edge reported a reason.
   */
  readonly failureReason?: string | undefined;
  /** Signed streaming URL for in-app playback — only present when `status` is `"ready"`. */
  readonly videoUrl?: string | undefined;
  /** Signed download URL (forces a save-as, unlike `videoUrl`) — only present when `status` is `"ready"`. */
  readonly downloadUrl?: string | undefined;
}

export interface CompilationListProps {
  readonly items: readonly CompilationListItem[];
  readonly isLoading?: boolean | undefined;
  readonly error?: boolean | undefined;
  /** Total matching the filters, across every page — drives the page count. */
  readonly totalCount?: number | undefined;
  /** Current 1-based page. */
  readonly page?: number | undefined;
  readonly pageSize?: number | undefined;
  readonly onPageChange?: ((page: number) => void) | undefined;
  /** True while a read is in flight; disables the pagination controls. */
  readonly isFetching?: boolean | undefined;
  readonly onWatch?: ((item: CompilationListItem) => void) | undefined;
  readonly onDownload?: ((item: CompilationListItem) => void) | undefined;
}

// ---------------------------------------------------------------------------
// CompilationList
// ---------------------------------------------------------------------------

export function CompilationList({
  items,
  isLoading,
  error,
  totalCount,
  page,
  pageSize,
  onPageChange,
  isFetching,
  onWatch,
  onDownload,
}: CompilationListProps) {
  const { theme } = useUnistyles();

  const keyExtractor = useCallback((item: CompilationListItem) => item.id, []);

  const renderItem = useCallback(
    ({ item }: ListRenderItemInfo<CompilationListItem>) => (
      <CompilationRow
        status={item.status}
        staleQueued={item.staleQueued}
        failureReason={item.failureReason}
        cameraLabel={item.cameraLabel}
        requestedAt={item.requestedAt}
        rangeStart={item.rangeStart}
        rangeEnd={item.rangeEnd}
        {...(onWatch && item.videoUrl ? { onWatch: () => onWatch(item) } : {})}
        {...(onDownload && item.downloadUrl ? { onDownload: () => onDownload(item) } : {})}
      />
    ),
    [onWatch, onDownload],
  );

  /**
   * Rendered under the list rather than as a `ListFooterComponent` so it stays
   * put while the rows scroll — `variant="bar"` is the filled strip the
   * component is designed to sit in under a table.
   *
   * Shown whenever there are rows, not only once they spill onto a second
   * page: it carries the "Showing 10 of 24" summary, which is useful on a
   * single page — and hiding it below the threshold made the control vanish
   * entirely for a tenant with a handful of compilations. `Pagination` renders
   * its own single-page state, with the navigation buttons disabled.
   */
  const pagination =
    totalCount != null && onPageChange ?
      <Pagination
        variant="bar"
        totalItems={totalCount}
        page={page}
        pageSize={pageSize}
        onPageChange={onPageChange}
        // Numbered paging only. The page size is fixed, so offering a selector
        // would be a control that changes nothing the reader asked for; the
        // summary still reports the count as plain text.
        showPageSizeSelect={false}
        disabled={isFetching ?? false}
      />
    : null;

  if (isLoading && items.length === 0) {
    return (
      <View style={styles.skeletonList}>
        <CompilationRowSkeleton />
        <CompilationRowSkeleton />
        <CompilationRowSkeleton />
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.centeredState}>
        <Text textStyle="sm/medium" color="danger">
          Unable to load compilations. Please try again later.
        </Text>
      </View>
    );
  }

  if (!isLoading && items.length === 0) {
    return (
      <View style={styles.centeredState}>
        <Text textStyle="sm/regular" color="textMuted">
          No compilations found
        </Text>
      </View>
    );
  }

  return (
    <View flex={1}>
      <Animated.FlatList
        data={items as CompilationListItem[]}
        renderItem={renderItem}
        keyExtractor={keyExtractor}
        ItemSeparatorComponent={Separator}
        removeClippedSubviews={false}
        style={{ flex: 1 }}
        contentContainerStyle={{
          padding: theme.spacing.xl,
        }}
        showsVerticalScrollIndicator
      />
      {pagination}
    </View>
  );
}

CompilationList.displayName = "CompilationList";

// ---------------------------------------------------------------------------
// Separator
// ---------------------------------------------------------------------------

function Separator() {
  const { theme } = useUnistyles();
  return <View style={{ height: theme.spacing.md }} />;
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles = StyleSheet.create((theme) => ({
  centeredState: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing["4xl"],
  },
  skeletonList: {
    padding: theme.spacing.xl,
    gap: theme.spacing.md,
  },
}));
